<script setup lang="ts">
import type { ConfiguredCoverageBenefit } from '~/config'
import { ContigoMarketPlacePreset } from '~/theme/preset'

interface Props {
  benefit: ConfiguredCoverageBenefit
  emisorId?: number | null
  planCode?: string | null
  showBar?: boolean
  template?: 'trait' | 'bar' | 'highlight'
}

const props = defineProps<Props>()

const { LocalConfigurationStore } = stores()

const associatedCompany = computed(() =>
  LocalConfigurationStore.value?.associatedCompanies?.find(
    (ac) => ac.argentoInsuranceId == props.emisorId
  )
)

const aplicableCoverageBenefits = computed(() => {
  if (!props.planCode) return

  const aplicableCoverageBenefits =
    LocalConfigurationStore.value?.associatedComaniesCoverageBenefits?.find((accb) => {
      const byCompany = accb.associatedCompanyCode === associatedCompany.value?.code
      if (byCompany) return true
      return accb.associatedCompanyCode === '_default'
    })?.aplicableCoverageBenefits

  const currentBenefit = aplicableCoverageBenefits?.find(
    (cb) => cb.identifier === props.benefit.identifier
  )

  const currentPlanAplicableCoverageBenefits = currentBenefit?.values.find(
    (v) => v.planCode === props.planCode
  )

  return {
    benefit: props.benefit,
    data: currentPlanAplicableCoverageBenefits,
  }
})

const applicableCoverageBenefitValue = ({
  defaultMaxPercentage,
}: {
  defaultMaxPercentage?: boolean
} = {}) => {
  const value = aplicableCoverageBenefits.value

  if (!value?.data)
    return {
      label: '',
      value: 0,
      percentage: 0,
    }

  let maxValue: number | undefined = value.benefit.maxValue
  let label: string = (value.data?.amount ?? 0).toString()
  let rawAmount = value.data?.amount ?? 0

  switch (value.data?.measure) {
    case '%':
      maxValue ??= 100
      label = `${value.data.amount}${value.data.measure}`
      break
    case 'MM':
      maxValue ??= 2
      label = `${value.data.amount} ${value.data.measure}`
      rawAmount = (value.data.amount ?? 0) * 1000000
      break
    case '$':
      maxValue ??= 500000
      label = `${value.data.measure}${formatCurrency({ price: value.data.amount ?? 0 })}`
      break
    case 'US$':
    case 'EUR€':
      maxValue ??= 20000
      label = `${value.data.measure} ${formatCurrency({ price: value.data.amount ?? 0 })}`
      break
    case 'K':
      maxValue ??= 1000000
      label = `${formatCurrency({ price: value.data.amount ?? 0 })} ${value.data.measure}`
      rawAmount = (value.data.amount ?? 0) * 1000
      break
    case 'days':
      maxValue ??= 30
      label = `${value.data.amount} días`
      break
    default:
      maxValue ??= value.data?.amount
      break
  }

  let percentage = 0
  if (rawAmount && maxValue) percentage = (rawAmount * 100) / maxValue
  if (value.data && (percentage > 100 || (percentage === 0 && defaultMaxPercentage)))
    percentage = 100

  return {
    label,
    value: value.data?.amount ?? 0,
    percentage,
  }
}

const brandColor = ContigoMarketPlacePreset.semantic.primary[50]
</script>

<template>
  <div
    v-if="template === 'trait'"
    class="flex flex-col justify-between items-center text-center relative size-full"
    :class="aplicableCoverageBenefits?.data ? 'opacity-100' : 'opacity-25'"
  >
    <div
      v-if="benefit.visible.includes('details')"
      class="absolute top-0 right-0 pr-[8%]"
    >
      *
    </div>
    <template v-if="benefit.iconUrl">
      <ImageComponent
        :src="benefit.iconUrl"
        :alt="benefit.identifier"
        class="rounded-md size-8 invert"
      />
    </template>
    <template v-else>
      <span
        class="icon-[ph--shield-check-bold]"
        :style="{
          color: brandColor,
          fontSize: '2rem',
        }"
      />
    </template>
    <div class="text-[0.6rem] leading-none">{{ benefit.tag ?? benefit.text }}</div>
    <PvProgressBar
      v-if="showBar"
      :value="applicableCoverageBenefitValue({ defaultMaxPercentage: true })?.percentage"
      :show-value="false"
      class="w-full h-[3px] mt-1"
    />
  </div>
  <div
    v-if="template === 'bar' && applicableCoverageBenefitValue()?.percentage !== 0"
    class="flex flex-col gap-1 relative"
  >
    <div class="text-xs leading-none">{{ benefit.text }}</div>
    <div class="flex items-stretch h-5">
      <div
        class="text-xs font-bold h-full flex items-center z-10 px-2 bg-primary text-white text-nowrap rounded-l-full"
      >
        {{ applicableCoverageBenefitValue()?.label }}
      </div>
      <PvProgressBar
        :value="applicableCoverageBenefitValue()?.percentage"
        class="h-5 relative z-0 w-full flex-grow rounded-r-full"
        :show-value="false"
      />
    </div>
  </div>
  <div v-if="template === 'highlight' && applicableCoverageBenefitValue()?.value > 0">
    <div
      class="border-primary rounded-md border flex items-stretch justify-between overflow-hidden text-xs"
    >
      <div class="px-2 py-1">
        {{ benefit.text }}
      </div>
      <div
        v-if="applicableCoverageBenefitValue()?.label"
        class="bg-primary flex items-center justify-center font-bold py-1 px-2 text-white text-nowrap"
      >
        {{ applicableCoverageBenefitValue()?.label }}
      </div>
    </div>
  </div>
</template>
